import { useLayoutEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';

/**
 * Prose that is clamped to `lines` and expands on tap. The "більше/менше"
 * toggle only shows up when the text is actually cut off, so short notes stay
 * exactly as compact as they are now.
 *
 * Wrapping is set to break on word boundaries first (`wordBreak: normal`) and
 * fall back to mid-word breaks only for a single word too long for the line —
 * that is what stops narrow columns from shredding Ukrainian words into
 * two-letter fragments.
 */
const ExpandableText = ({
  children,
  lines = 2,
  color = '#1E2A4A',
  fontSize = 13,
  sx,
}) => {
  const ref = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [clamped, setClamped] = useState(false);

  useLayoutEffect(() => {
    const node = ref.current;
    // While expanded there is nothing to measure — keep the last verdict so the
    // "менше" toggle does not disappear from under the user's finger.
    if (!node || expanded) return undefined;

    const measure = () => setClamped(node.scrollHeight - node.clientHeight > 1);
    measure();

    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [children, lines, expanded]);

  return (
    <Box sx={sx}>
      <Typography
        ref={ref}
        lang="uk"
        variant="body2"
        sx={{
          color,
          fontSize,
          lineHeight: 1.45,
          hyphens: 'auto',
          overflowWrap: 'break-word',
          wordBreak: 'normal',
          whiteSpace: 'pre-line',
          ...(expanded
            ? {}
            : {
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: lines,
                overflow: 'hidden',
              }),
        }}
      >
        {children}
      </Typography>
      {(clamped || expanded) && (
        <Typography
          component="button"
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          sx={{
            mt: 0.25,
            p: 0,
            border: 'none',
            bgcolor: 'transparent',
            color: '#4357AD',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'inherit',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          {expanded ? 'менше' : 'більше'}
        </Typography>
      )}
    </Box>
  );
};

export default ExpandableText;
